'use client';

import { useState, useEffect, useMemo } from 'react';
import { ReactNode } from 'react';

// Import the TerminalTabs components
import BinanceTerminalTabs from '../binance/TerminalTabs';
import NinTerminalTabs from '../nintrade/stocks/TerminalTabs';
import NinWeb3TerminalTabs from '../nintrade/web3/TerminalTabs';
import GrowwTerminalTabs from '../groww/TerminalTabs';
import ZerodhaTerminalTabs from '../zerodha/TerminalTabs';

// Import the OrderBook components
import BinanceOrderBook from '../binance/OrderBook';
import NinOrderBook from '../nintrade/stocks/OrderBook';
import NinWeb3OrderBook from '../nintrade/web3/OrderBook';
import GrowwOrderBook from '../groww/OrderBook';
import ZerodhaOrderBook from '../zerodha/OrderBook';

// Import the Watchlist components
import BinanceWatchlist from '../binance/Watchlist';
import NinWatchlist from '../nintrade/stocks/Watchlist';
import NinWeb3Watchlist from '../nintrade/web3/Watchlist';
import GrowwWatchlist from '../groww/Watchlist';
import ZerodhaWatchlist from '../zerodha/Watchlist';

// Import the BuySell components
import BinanceBuySell from '../binance/BuySell';
import NinBuySell from '../nintrade/stocks/BuySell';
import NinWeb3BuySell from '../nintrade/web3/BuySell';
import GrowwBuySell from '../groww/BuySell';
import ZerodhaBuySell from '../zerodha/BuySell';

import { getSecureItem } from '../utils/secureStorage'

// Define the possible broker types
export type BrokerType = 'nintrade' | 'binance' | 'ninweb3' | 'groww' | 'zerodha' | 'ninx';

interface UseBrokerSelectionResult {
  selectedBroker: BrokerType;
  TerminalTabsComponent: React.ComponentType<any>;
  OrderBookComponent: React.ComponentType<any>;
  WatchlistComponent: React.ComponentType<any>;
  BuySellComponent: React.ComponentType<any>;
}

/**
 * Custom hook to get the currently selected broker from localStorage
 * and return the appropriate TerminalTabs component
 * 
 * @param defaultBroker The default broker to use if none is found in localStorage
 * @returns An object containing the selected broker and the appropriate TerminalTabs component
 */
export function useBrokerSelection(defaultBroker: BrokerType = 'ninx'): UseBrokerSelectionResult {
  const [selectedBroker, setSelectedBroker] = useState<BrokerType>(defaultBroker);

  useEffect(() => {
    // Only run in browser environment
    if (typeof window !== 'undefined') {
      const storedBroker = getSecureItem('broker_enabled');
      
      // Check if the stored broker is valid
      if (storedBroker && [
        'nintrade', 'binance', 'ninweb3', 'groww', 'zerodha', 'ninx'
      ].includes(storedBroker)) {
        setSelectedBroker(storedBroker as BrokerType);
      }
    }
  }, [defaultBroker]);

  // Memoize the TerminalTabs component based on the selected broker
  const TerminalTabsComponent = useMemo(() => {
    switch (selectedBroker) {
      case 'binance':
        return BinanceTerminalTabs;
      case 'ninx':
        return BinanceTerminalTabs;
      case 'ninweb3':
        return NinWeb3TerminalTabs;
      case 'groww':
        return GrowwTerminalTabs;
      case 'zerodha':
        return ZerodhaTerminalTabs;
      case 'nintrade':
      default:
        return NinTerminalTabs;
    }
  }, [selectedBroker]);

  // Memoize the OrderBook component based on the selected broker
  const OrderBookComponent = useMemo(() => {
    switch (selectedBroker) {
      case 'binance':
        return BinanceOrderBook;
      case 'ninx':
        return BinanceOrderBook;
      case 'ninweb3':
        return NinWeb3OrderBook;
      case 'groww':
        return GrowwOrderBook;
      case 'zerodha':
        return ZerodhaOrderBook;
      case 'nintrade':
      default:
        return NinOrderBook;
    }
  }, [selectedBroker]);

  // Memoize the Watchlist component based on the selected broker
  const WatchlistComponent = useMemo(() => {
    switch (selectedBroker) {
      case 'binance':
        return BinanceWatchlist;
      case 'ninx':
        return BinanceWatchlist;
      case 'ninweb3':
        return NinWeb3Watchlist;
      case 'groww':
        return GrowwWatchlist;
      case 'zerodha':
        return ZerodhaWatchlist;
      case 'nintrade':
      default:
        return NinWatchlist;
    }
  }, [selectedBroker]);

  // Memoize the BuySell component based on the selected broker
  const BuySellComponent = useMemo(() => {
    switch (selectedBroker) {
      case 'binance':
        return BinanceBuySell;
      case 'ninx':
        return BinanceBuySell;
      case 'ninweb3':
        return NinWeb3BuySell;
      case 'groww':
        return GrowwBuySell;
      case 'zerodha':
        return ZerodhaBuySell;
      case 'nintrade':
      default:
        return NinBuySell;
    }
  }, [selectedBroker]);

  return {
    selectedBroker,
    TerminalTabsComponent,
    OrderBookComponent,
    WatchlistComponent,
    BuySellComponent
  };
}